import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useAuth } from '../contexts/AuthContext';
import { ChefHeader } from '../components/ios';
import { Star } from 'lucide-react';
import { MenuItemCard } from './MenuPage';

// "View All" target for the Admin's Choice sections on MenuPage — a grid of
// every menu item flagged isFeatured, optionally filtered by ?type=food|retail.
const FOOD_TYPES = new Set([
  'Fast Food', 'Bar & Grill', 'Pizza', 'KFC', 'BBQ Grill', 'Seafood Restaurant', 'Cafe', 'Bakery', 'Asian', 'Indian', 'Roti/Patty', 'Juice Stand', 'Vegetarian', 'Snack Boxes', 'Latin', 'Mexican', 'Sushi', 'Steak House', 'Local Restaurant'
]);

export default function FeaturedItemsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type'); // 'food' | 'retail' | null
  const { db } = useFirebase();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { currentUser } = useAuth();

  const [items, setItems] = useState([]);
  const [restaurantById, setRestaurantById] = useState({});
  const [loading, setLoading] = useState(true);

  const islandValue = (typeof window !== 'undefined' &&
    localStorage.getItem('chef_selected_island') === 'nevis')
    ? 'Nevis'
    : 'St. Kitts';

  useEffect(() => {
    if (!db) return;
    let cancelled = false;

    (async () => {
      try {
        // Eligible restaurants — same gate as MenuPage's featured filter
        const rSnap = await getDocs(collection(db, 'restaurants'));
        const eligible = rSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(r =>
            r.island === islandValue &&
            r.isActive !== false &&
            r.isAcceptingOrders !== false
          );
        const byId = Object.fromEntries(eligible.map(r => [r.id, r]));

        const results = await Promise.all(
          eligible.map(async (r) => {
            try {
              const q = query(
                collection(db, 'restaurants', r.id, 'menuItems'),
                where('isFeatured', '==', true)
              );
              const snap = await getDocs(q);
              return snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                restaurantId: r.id,
                restaurantName: r.name,
                restaurantType: r.restaurantType || 'Other',
              }));
            } catch {
              return [];
            }
          })
        );

        if (cancelled) return;
        const all = results.flat()
          .filter(i => i.isAvailable !== false)
          .filter(i =>
            typeParam === 'food' ? FOOD_TYPES.has(i.restaurantType) :
            typeParam === 'retail' ? !FOOD_TYPES.has(i.restaurantType) :
            true
          )
          .sort((a, b) =>
            (b.featuredAt?.toMillis?.() ?? b.featuredAt?.seconds ?? 0) -
            (a.featuredAt?.toMillis?.() ?? a.featuredAt?.seconds ?? 0));
        setItems(all);
        setRestaurantById(byId);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [db, islandValue, typeParam]);

  const title =
    typeParam === 'food' ? 'Featured Food' :
    typeParam === 'retail' ? 'Featured Retail' :
    "Admin's Choice";

  const handleToggleFavorite = (item) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    toggleFavorite(item);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <ChefHeader title={title} />

      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="loading-spinner" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-32 space-y-4">
          <Star size={48} className="text-[#8E8E93]" strokeWidth={1.5} />
          <h2 className="text-[17px] font-semibold text-gray-900">No Featured Items</h2>
          <p className="text-[15px] text-[#6D6D72] text-center">
            Admin picks will appear here
          </p>
        </div>
      ) : (
        <div className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto pt-4 pb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-4 px-[7px]">
            {items.map(item => {
              const restaurant = restaurantById[item.restaurantId];
              const imageURL = item.menuItemImageURL || restaurant?.bannerURL || '';
              return (
                <div key={item.id} className="flex flex-col gap-1">
                  <button
                    onClick={() => navigate(`/restaurant/${item.restaurantId}?businessType=${typeParam || 'food'}`)}
                    className="relative w-full h-[100px] rounded-xl overflow-hidden bg-white cursor-pointer shadow-[0_2px_5px_rgba(0,0,0,0.1)] ring-1 ring-black/10"
                  >
                    {/* Image */}
                    {imageURL ? (
                      <>
                        <img
                          src={imageURL}
                          alt=""
                          aria-hidden
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover blur-md scale-125 opacity-80"
                        />
                        <img
                          src={imageURL}
                          alt={item.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </>
                    ) : (
                      <div className="absolute inset-0 bg-gray-400/30 flex items-center justify-center">
                        <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 2 0 002-2V6a6 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}

                    {/* Gradient */}
                    <div
                      className="absolute inset-0"
                      style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 55%, transparent 100%)' }}
                    />

                    {/* Admin's Choice badge */}
                    <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 text-white text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-[#FF3B30]">
                      <Star size={8} className="text-[#FFC800] fill-[#FFC800]" />
                      Admin's Choice
                    </div>

                    {/* Price */}
                    <div className="absolute bottom-3 right-3">
                      <span className="text-[11px] font-bold text-[#30D158] drop-shadow-[0_1px_0_rgba(0,0,0,0.8)]">
                        ${item.price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </button>

                  {/* Text info below card */}
                  <div className="flex flex-col gap-0.5">
                    <h3 className="text-[13px] font-semibold text-gray-900 leading-tight truncate">
                      {item.name}
                    </h3>
                    <p className="text-[11px] text-gray-600 leading-tight truncate">
                      {restaurant?.name || item.restaurantName || 'Local business'}
                    </p>
                    <span className="text-[10px] text-gray-500 px-2 py-0.5 bg-gray-100 rounded-md w-fit">
                      {item.category}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
